import React, { useEffect, useState } from "react";
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    TextField, 
    MenuItem, 
    FormControlLabel, 
    Checkbox, 
    DialogActions, 
    Button 
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";

import {
    UserProfile, 
    DementiaDiagnoses,
    FamiliarLevels, 
    EducationLevels, 
    HabitAnswers, 
    MocaScore
} from "../types/UserProfile";
import { useSelector } from "react-redux";
import { AppState } from "../reducers";
import { RoleType } from "../types/UserRole";
import { Login } from "../types/Login";
import SimpleSnackBar, { SnackBarProp } from "./SimpleSnackBar";
import { updateUserProfile } from "../api";
import { StyledCircularProgress } from "../StyledComponents";
import { LoadableAllCenterData } from "../pages/UserProfilesPage";
import { EditMocaScorePanel } from "./EditMocaScorePanel";
// import { ConnectingAirportsOutlined } from "@mui/icons-material";

interface AddUserDialogProps {
    user?: UserProfile;
    onClose: () => void,
    isOpen: boolean,
    allCenter?: LoadableAllCenterData,
}

export default function AddUserDialog(props: AddUserDialogProps) {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const userRole = useSelector<AppState, RoleType | null>(state => state.userRole);
    const { t } = useTranslation();
    const blankUserProfile: UserProfile = {
        user_id: "",
        name: "",
        gender: "",
        avatar_url: "",
        year_of_birth: null,
        dementia_diagnosis: "unknown",
        occupation: "",
        smoke: "unknown",
        drink_alcohol: "unknown",
        avg_walking_time: undefined,
        moca_score: null,
        is_dementia_mode: false,
        carer: "",
        followUp: "",
        memberId: ""
    };

    const blankUserMoca: MocaScore = {
        visuospatialExecutive: null,
        naming: null,
        attention: null,
        language: null,
        abstraction: null,
        delayedRecall: null,
        orientation: null,
    }

    const newLogin: Login = {
        email: "",
        password: "",
    }

    const [loaded, setLoaded] = useState<boolean>(false);
    const [userProfile, setUserProfile] = useState<UserProfile>(blankUserProfile);
    const [login, setLogin] = useState<Login>(newLogin);
    const [nameError, setNameError] = useState<boolean>(false);
    const [centerError, setCenterError] = useState<boolean>(false);
    // const [genderError, setGenderError] = useState<boolean>(false);
    // const [yearOfBirthError, setYearOfBirthError] = useState<boolean>(false);
    const [emailError, setEmailError] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [mocaScore, setMocaScore] = useState<MocaScore | undefined | null>();
    const [editMocaScorePanelOpen, setEditMocaScorePanelOpen] = useState<boolean>(false);
    const [centerArr, setCenterArr] = useState<any>();
    const [centerName, setCenterName] = useState<string>("")

    const closeErrorSnackBar = () => {
        setErrorSnackBar({ ...errorSnackBar, "open": false });
    }

    const [errorSnackBar, setErrorSnackBar] = useState<SnackBarProp>({
        open: false,
        onClose: closeErrorSnackBar,
        message: "",
    });

    useEffect(() => {
        if (props.user !== undefined) {
            setUserProfile(props.user);
            setMocaScore(props.user.moca_score);
        } else {
            setUserProfile(blankUserProfile);
            setMocaScore(blankUserMoca);
        }

        if (props.allCenter?.kind === "loaded") {
            setCenterArr(props.allCenter.data)
        }
    }, [props]);

    useEffect(() => {
        if (userProfile.center !== undefined && userProfile.user_id !== "" && !loaded) {
            // _getUserParent(userProfile.user_id);
            // setLoaded(true);
        }
    }, [userProfile])

    const _getUserParent = async (id: string) => {
        // const center = await getUserParent(id);
        // if (center) {
        //     setUserProfile({ ...userProfile, center: center });
        // }

        // const center = ;
        // console.log(Object.values(centerArr))
    }
    
    function validateEmail(email: string) {
        const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email.toLowerCase());
    }

    function validatePassword(pw: string) {
        const regex = /^.{6,}$/;
        return regex.test(pw);
    }

    const onConfirmMocaScore = (scores: MocaScore | null) => {
        setMocaScore(scores);
        setUserProfile(prev => { return { ...prev, moca_score: scores } });
    }

    const submitButtonClicked = async () => {
        let hasError = false;
        let hasValidationError = false;
        setNameError(false);
        setCenterError(false);
        setEmailError(false);
        setPasswordError(false);

        if (userProfile.name === "") {
            setNameError(true);
            hasError = true;
        }
        if (userRole === "admin" && (userProfile.center === undefined || userProfile.center === "")) {
            setCenterError(true);
            hasError = true;
        }
        if (props.user === undefined && login.email === "") {
            setEmailError(true);
            hasError = true;
        }
        if (props.user === undefined && login.password === "") {
            setPasswordError(true);
            hasError = true;
        }

        if (!props.user) {
            if (!validateEmail(login.email)) {
                setEmailError(true);
                hasValidationError = true;
            }
      
            if (!validatePassword(login.password)) {
                setPasswordError(true);
                hasValidationError = true;
            }
        }
      
        if (hasValidationError) return;

        if (hasError === false) {
            setIsSubmitting(true);
            try {
                if (userRole !== null) {
                    if (userProfile.use_smartphone_level === "unknown") delete userProfile.use_smartphone_level;
                    if (userProfile.education_level === "unknown") delete userProfile.education_level;
                    if (userProfile.mahjong_level === "unknown") delete userProfile.mahjong_level;
                    if (userProfile.smoke === "unknown") delete userProfile.smoke;
                    if (userProfile.drink_alcohol === "unknown") delete userProfile.drink_alcohol;
                    await updateUserProfile(userProfile, userRole, login);
                    props.onClose();
                }
            } catch (e) {
                // console.log(e)
                setErrorSnackBar({ 
                    ...errorSnackBar, 
                    "open": true, 
                    message: t("add_user_dialog.message_email_exists"),
                });
            }
            setIsSubmitting(false);
        } else {
            setErrorSnackBar({
                open: true,
                onClose: closeErrorSnackBar,
                message: t("error.multiply_empty"),
            });
        }
        return;
    }

    const onInputChange = (name: keyof UserProfile) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserProfile(
            { ...userProfile, 
                [name]: (name === "year_of_birth") ? Number(event.target.value) 
                : (name === "is_dementia_mode") ? event.target.checked 
                : event.target.value 
            }
        );
    }

    const onLoginInputChange = (name: keyof Login) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setLogin({ ...login, [name]: event.target.value });
    }

    const calculateMocaScore = (scores: MocaScore): string => {
        // Check whether the score is of right type (because some are old types i.e. number only)
        if (scores && Number.isInteger(scores)) {
            return t("msg.format_need_update");
        } else if (scores && !Number.isInteger(scores) && scores.visuospatialExecutive === undefined) {
            return t("msg.invalid_record");
        }
        var total = 0;
        for (let [key, val] of Object.entries(scores)) {
            total += val;
        }
        return `${total} ${t("units_general.pt")}`;
    }

    return (
        <Dialog 
            onClose={props.onClose}
            aria-labelledby="simple-dialog-title"
            open={props.isOpen}
        >
            <SimpleSnackBar 
                open={errorSnackBar.open} 
                onClose={closeErrorSnackBar}
                message={errorSnackBar.message}
            />
            <DialogTitle id="simple-dialog-title">
                {props.user !== undefined ? t("add_user_dialog.text_modify_user") : t("add_user_dialog.text_add_user")}
            </DialogTitle>
            <DialogContent>
                {props.user === undefined &&
                    <div>
                        <div>{t("add_user_dialog.text_acc_data")}</div>
                        <TextField
                            required
                            margin="dense"
                            id="email"
                            error={emailError}
                            label={t("acc_key.email_address")}
                            helperText={emailError? t("error.email_format") : ""}
                            type="text"
                            onChange={onLoginInputChange("email")}
                            fullWidth
                        />
                        <TextField
                            required
                            margin="dense"
                            id="password"
                            error={passwordError}
                            label={t("acc_key.pw_format")}
                            helperText={passwordError? t("error.pw_format") : ""}
                            type="password"
                            onChange={onLoginInputChange("password")}
                            fullWidth
                        />
                    </div>
                }
                <TextField
                    required
                    margin="dense"
                    error={nameError}
                    id="name"
                    label={t("common.name")}
                    helperText={nameError && ("")}
                    type="text"
                    value={userProfile.name?? ""}
                    onChange={onInputChange("name")}
                    fullWidth
                />

                <TextField
                    margin="dense"
                    id="gender"
                    label={t("common.gender")}
                    select
                    value={userProfile.gender?? ""}
                    onChange={onInputChange("gender")}
                    fullWidth
                >
                    <MenuItem key="none" value={""}></MenuItem>
                    <MenuItem key="M" value={"M"}>{t("gender.male")}</MenuItem>
                    <MenuItem key="F" value={"F"}>{t("gender.female")}</MenuItem>
                </TextField>

                <TextField
                    margin="dense"
                    id="year_of_birth"
                    label={t("common.year_of_birth")}
                    type="number"
                    InputProps={{
                        inputProps: { 
                            max: new Date().getFullYear(), 
                            min: 1900
                        }
                    }}
                    value={userProfile.year_of_birth?? ""}
                    onChange={onInputChange("year_of_birth")}
                    fullWidth
                />
                {props.allCenter !== undefined 
                    && props.allCenter.kind === "loading"
                    && <StyledCircularProgress />
                }
                {props.allCenter !== undefined
                    && userRole === "admin"
                    && props.allCenter.kind === "loaded"
                    &&  <TextField
                            required
                            margin="dense"
                            id="center"
                            label={t("common.center")}
                            error={centerError}
                            select
                            helperText={centerError? t("add_user_dialog.helper_text_center"): ""}
                            value={userProfile.center?? ""}
                            onChange={onInputChange("center")}
                            fullWidth
                        >
                            <MenuItem key={"emptyCenter"} value={""}></MenuItem>
                            {props.allCenter.data.map(x => {
                                if (x !== null && x.name !== "") {
                                    return ( <MenuItem key={x.uid} value={x.name}>{x.name}</MenuItem> )
                                }
                            })}
                        </TextField>
                }

                <div style={{ 
                        margin: "20px 0", 
                        height: 34, 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "space-between"
                    }}
                >
                    <div style={{ fontSize: 18 }}>
                        {t("add_user_dialog.text_moca_score")}
                        {mocaScore ? `${calculateMocaScore(mocaScore)}` : t("add_user_dialog.text_moca_score_not_tested")}
                    </div>
                    <Button 
                        color="primary" 
                        variant="contained" 
                        size="small" 
                        onClick={() => setEditMocaScorePanelOpen(true)}
                    >
                        {t("add_user_dialog.butt_modify_moca")}
                    </Button>
                </div>

                <TextField
                    id="dementia_diagnosis"
                    select
                    label={t("common.dementia_diagnosis")}
                    value={userProfile.dementia_diagnosis?? "unknown"}
                    onChange={onInputChange('dementia_diagnosis')}
                    margin="normal"
                    fullWidth
                >
                    {DementiaDiagnoses.map(option => (
                        <MenuItem key={option} value={option}>
                            {t(`dementia_diagnosis.${option}` as unknown as TemplateStringsArray)}
                        </MenuItem>
                    ))}
                </TextField>

                <FormControlLabel
                    control={
                        <Checkbox 
                            checked={userProfile.is_dementia_mode?? false}
                            onChange={onInputChange("is_dementia_mode")}
                            name="is_dementia_mode"
                            color='primary'
                        />
                    }
                    label={t("add_user_dialog.label_dementia_mode")}
                    style={{ paddingTop: 10 }}
                />

                <TextField
                    margin="dense"
                    id="occupation"
                    label={t("common.occupation_previous_or_present")}
                    type="text"
                    value={userProfile.occupation?? ""}
                    onChange={onInputChange("occupation")}
                    fullWidth
                />

                <TextField
                    id="use_smartphone_level"
                    select
                    label={t("common.use_smartphone_level")}
                    value={userProfile.use_smartphone_level?? "unknown"}
                    onChange={onInputChange('use_smartphone_level')}
                    margin="normal"
                    fullWidth
                >
                    {FamiliarLevels.map(option => (
                        <MenuItem key={option} value={option}>
                            {t(`use_smartphone_level.${option}` as unknown as TemplateStringsArray)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="education_level"
                    select
                    label={t("common.education_level")}
                    value={userProfile.education_level?? "unknown"}
                    onChange={onInputChange('education_level')}
                    margin="normal"
                    fullWidth
                >
                    {EducationLevels.map(option => (
                        <MenuItem key={option} value={option}>
                            {t(`education_level.${option}` as unknown as TemplateStringsArray)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="mahjong_level"
                    select
                    label={t("common.mahjong_level")}
                    value={userProfile.mahjong_level?? "unknown"}
                    onChange={onInputChange('mahjong_level')}
                    margin="normal"
                    fullWidth
                >
                    {FamiliarLevels.map(option => (
                        <MenuItem key={option} value={option}>
                            {t(`mahjong_level.${option}` as unknown as TemplateStringsArray)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="smoke"
                    select
                    label={t("common.smoke")}
                    value={userProfile.smoke?? "unknown"}
                    onChange={onInputChange('smoke')}
                    margin="normal"
                    fullWidth
                >
                    {HabitAnswers.map(option => (
                        <MenuItem key={option} value={option}>
                            {t(`common_level.${option}` as unknown as TemplateStringsArray)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    id="drink_alcohol"
                    select
                    label={t("common.drink_alcohol")}
                    value={userProfile.drink_alcohol?? "unknown"}
                    onChange={onInputChange('drink_alcohol')}
                    margin="normal"
                    fullWidth
                >
                    {HabitAnswers.map(option => (
                        <MenuItem key={option} value={option}>
                            {t(`common_level.${option}` as unknown as TemplateStringsArray)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    margin="dense"
                    id="eHealthId"
                    label={t("common.eHealthId")}
                    type="text"
                    value={userProfile.eHealthId?? ""}
                    onChange={onInputChange("eHealthId")}
                    fullWidth
                />

                <TextField
                    id="carer"
                    label={t("common.carer")}
                    type="text"
                    value={userProfile.carer?? ""}
                    onChange={onInputChange("carer")}
                    fullWidth
                    margin="dense"
                />

                <TextField
                    id="followUp"
                    label={t("common.followUp")}
                    type="text"
                    value={userProfile.followUp?? ""}
                    onChange={onInputChange("followUp")}
                    fullWidth
                    margin="dense"
                />

                <TextField
                    id="memberId"
                    label={t("common.memberId")}
                    type="text"
                    value={userProfile.memberId?? ""}
                    onChange={onInputChange("memberId")}
                    fullWidth
                    margin="dense"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} color="primary">
                    {t("butt.close")}
                </Button>
                <LoadingButton loading={isSubmitting} color="primary" onClick={submitButtonClicked} variant="contained">
                    {t("butt.save")}
                </LoadingButton>
            </DialogActions>

            <EditMocaScorePanel 
                open={editMocaScorePanelOpen} 
                initialValue={mocaScore?? undefined}
                onClose={() => setEditMocaScorePanelOpen(false)} 
                onConfirm={onConfirmMocaScore} 
            />
        </Dialog >
    )
}